import gql from 'graphql-tag'
import { memoize } from 'lodash'

export const libraryFragment = gql`
  fragment Library on Library {
    id
    hash
    language
    group
    name
    grade {
      score
      value
    }
    usage {
      total
      totalUsed
    }
    policies {
      restrictedByOrg
      invalidVersion
      versionRestriction
      restrictedLicense
    }
    ossEnabled
    versions {
      current {
        version
        releaseDate
      }
      latest {
        version
        releaseDate
      }
    }
    licenses
    tags
    bugTrackers {
      name
      type
      creation
      ticketKey
      ticketUrl
      ticketTitle
    }
    applications {
      id
      appId
      name
      appLibraryStatus
    }
    mergedApplications {
      id
      appId
      name
      appLibraryStatus
    }
    vulnerabilities {
      name
      description
      visible
      references {
        href
      }
      severityCode
      severityValue
      confidentialityImpact
      integrityImpact
      availabilityImpact
      authentication
      accessVector
      accessComplexity
      hasCve
      hasCvss3
      cvss3Vector
      cvss3SeverityValue
      cvss3SeverityCode
    }
    libraryClassUsageCounts {
      appId
      usageCount
    }
  }
`

export const getApplicationPageQuery = memoize(
  (id) => gql`
      query ApplicationLibraryPage(
        $offset: Int!
        $limit: Int!
        $organizationID: ID!
        $orderBy: LibraryOrder
        $filterText: String
        $quickFilter: String
        $servers: [String!]
        $tags: [String!]
        $languages: [String!]
        $licenses: [String!]
        $grades: [String!]
        $status: [String!]
        $usage: [String!]
        $environments: [String!]
      ) {
        libraries(
          offset: $offset
          limit: $limit
          organizationID: $organizationID
          orderBy: $orderBy
          filters: {
            applications: ["${id}"]
            filterText: $filterText
            quickFilter: $quickFilter
            environments: $environments
            servers: $servers
            tags: $tags
            languages: $languages
            licenses: $licenses
            grades: $grades
            status: $status
            usage: $usage
            environments: $environments
          }
        ) {
          items {
            ...Library
          }
          total
        }
      }

      ${libraryFragment}
    `
)

export const getApplicationQuickFiltersQuery = memoize(
  (id) => gql`
      query ApplicationLibrariesQuickFilters(
        $organizationID: ID!
        $filterText: String
        $servers: [String!]
        $tags: [String!]
        $languages: [String!]
        $licenses: [String!]
        $grades: [String!]
        $status: [String!]
        $usage: [String!]
        $environments: [String!]
      ) {
        libraryQuickFilters(
          organizationID: $organizationID
          filters: {
            applications: ["${id}"]
            filterText: $filterText
            servers: $servers
            tags: $tags
            languages: $languages
            licenses: $licenses
            grades: $grades
            status: $status
            usage: $usage
            environments: $environments
          }
        ) {
          id
          name
          count
        }
      }
    `
)

export const getApplicationAggregationQuery = memoize(
  (id) => gql`
    query ApplicationLibraryAggregations(
      $organizationID: ID!
      $filterText: String
      $quickFilter: String
      $servers: [String!]
      $tags: [String!]
      $languages: [String!]
      $licenses: [String!]
      $grades: [String!]
      $status: [String!]
      $usage: [String!]
      $environments: [String!]
    ) {
      libraryAggregations(
        organizationID: $organizationID
        filters: {
          applications: ["${id}"]
          filterText: $filterText
          quickFilter: $quickFilter
          dateFilter: $dateFilter
          servers: $servers
          tags: $tags
          languages: $languages
          licenses: $licenses
          grades: $grades
          status: $status
          usage: $usage
          environments: $environments
        }
      ) {
        servers {
          key {
            id
            label
          }
          count
        }
        languages {
          key
          count
        }
        tags {
          key
          count
        }
        environments {
          key {
            id
            label
          }
          count
        }
        licenses {
          key
          count
        }
        grades {
          key
          count
        }
        status {
          key {
            id
            label
          }
          count
        }
        usage {
          key {
            id
            label
          }
          count
        }
      }
    }

    `
)

export const libraryQuery = gql`
  query GetLibrary($organizationID: ID!, $hash: String!, $language: String!) {
    library(organizationID: $organizationID, hash: $hash, language: $language) {
      ...Library
    }
  }

  ${libraryFragment}
`

export const organizationPageQuery = gql`
  query OrganizationLibraryPage(
    $offset: Int!
    $limit: Int!
    $organizationID: ID!
    $orderBy: LibraryOrder
    $filterText: String
    $quickFilter: String
    $applications: [String!]
    $servers: [String!]
    $tags: [String!]
    $languages: [String!]
    $licenses: [String!]
    $grades: [String!]
    $environments: [String!]
  ) {
    libraries(
      offset: $offset
      limit: $limit
      organizationID: $organizationID
      orderBy: $orderBy
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        servers: $servers
        applications: $applications
        tags: $tags
        languages: $languages
        licenses: $licenses
        grades: $grades
        environments: $environments
      }
    ) {
      items {
        ...Library
      }
      total
    }
  }

  ${libraryFragment}
`

export const organizationQuickFiltersQuery = gql`
  query LibraryQuickFilters(
    $organizationID: ID!
    $filterText: String
    $applications: [String!]
    $servers: [String!]
    $tags: [String!]
    $languages: [String!]
    $licenses: [String!]
    $grades: [String!]
    $environments: [String!]
  ) {
    libraryQuickFilters(
      organizationID: $organizationID
      filters: {
        filterText: $filterText
        servers: $servers
        applications: $applications
        tags: $tags
        languages: $languages
        licenses: $licenses
        grades: $grades
        environments: $environments
      }
    ) {
      id
      name
      count
    }
  }
`

export const organizationAggregationsQuery = gql`
  query OrganizationLibraryAggregations(
    $organizationID: ID!
    $filterText: String
    $quickFilter: String
    $applications: [String!]
    $servers: [String!]
    $tags: [String!]
    $languages: [String!]
    $licenses: [String!]
    $grades: [String!]
    $environments: [String!]
  ) {
    libraryAggregations(
      organizationID: $organizationID
      filters: {
        filterText: $filterText
        quickFilter: $quickFilter
        dateFilter: $dateFilter
        servers: $servers
        applications: $applications
        tags: $tags
        languages: $languages
        licenses: $licenses
        grades: $grades
        environments: $environments
      }
    ) {
      servers {
        key {
          id
          label
        }
        count
      }
      applications {
        key {
          id
          label
        }
        count
      }
      environments {
        key {
          id
          label
        }
        count
      }
      languages {
        key
        count
      }
      tags {
        key
        count
      }
      licenses {
        key
        count
      }
      grades {
        key
        count
      }
    }
  }
`

export const getServerPageQuery = memoize(
  (id) => gql`
    query ServerLibraryPage(
      $offset: Int!
      $limit: Int!
      $organizationID: ID!
      $orderBy: LibraryOrder
      $filterText: String
      $quickFilter: String
      $applications: [String!]
      $tags: [String!]
      $languages: [String!]
      $licenses: [String!]
      $grades: [String!]
    ) {
      libraries(
        offset: $offset
        limit: $limit
        organizationID: $organizationID
        orderBy: $orderBy
        filters: {
          servers: ["${id}"]
          filterText: $filterText
          quickFilter: $quickFilter
          applications: $applications
          tags: $tags
          languages: $languages
          licenses: $licenses
          grades: $grades
        }
      ) {
        items {
          ...Library
        }
        total
      }
    }

    ${libraryFragment}
  `
)

export const getServerQuickFiltersQuery = memoize(
  (id) => gql`
    query ServerLibrariesQuickFilters(
      $organizationID: ID!
      $filterText: String
      $applications: [String!]
      $tags: [String!]
      $languages: [String!]
      $licenses: [String!]
      $grades: [String!]
    ) {
      libraryQuickFilters(
        organizationID: $organizationID
        filters: {
          servers: ["${id}"]
          filterText: $filterText
          applications: $applications
          tags: $tags
          languages: $languages
          licenses: $licenses
          grades: $grades
        }
      ) {
        id
        name
        count
      }
    }
  `
)

export const getServerAggregationQuery = memoize(
  (id) => gql`
  query ServerLibraryAggregations(
    $organizationID: ID!
    $filterText: String
    $quickFilter: String
    $applications: [String!]
    $tags: [String!]
    $languages: [String!]
    $licenses: [String!]
    $grades: [String!]
  ) {
    libraryAggregations(
      organizationID: $organizationID
      filters: {
        servers: ["${id}"]
        filterText: $filterText
        quickFilter: $quickFilter
        dateFilter: $dateFilter
        applications: $applications
        tags: $tags
        languages: $languages
        licenses: $licenses
        grades: $grades
      }
    ) {
      applications {
        key {
          id
          label
        }
        count
      }
      languages {
        key
        count
      }
      tags {
        key
        count
      }
      licenses {
        key
        count
      }
      grades {
        key
        count
      }
    }
  }

  `
)
